import React from 'react';

import { Box, Typography, Grid, Paper, Checkbox, FormControlLabel, makeStyles, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Help as HelpIcon } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import FormDateInput from 'components/FormDateInput';
import FormRichTextInput from 'components/FormRichTextInput';
import FormTextInput from 'components/FormTextInput';
import Loader from 'components/Loader';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { ActionTemplate, ActionTemplateStep } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../ActionLibraryInstanceForm.types';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(4),
  },
  headerWithInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  summaryPaper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    marginBottom: theme.spacing(2),
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  stepOrder: {
    width: 40,
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  stepContent: {
    flexGrow: 1,
  },
  stepCheckbox: {
    marginLeft: theme.spacing(2),
  },
  infoIcon: {
    cursor: 'help',
    color: theme.palette.action.active,
    fontSize: '1.2rem',
  },
}));

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const StepTimeParameters: React.FC = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<ActionLibraryInstanceFormValues>();

  const { data: templateResponse, isLoading } = useQuery(
    ['ACTION_TEMPLATES_GET_SINGLE', values.templateId],
    () => actionTemplatesApi.getSingleActionTemplate(Number(values.templateId)),
    {
      enabled: !!values.templateId,
      onSuccess: data => {
        // Initialize overrides if not already set or if template changed
        // We only init if empty to preserve user changes if they navigate back/forth
        if (data.data.steps && values.stepOverrides.length === 0) {
          const initialOverrides = data.data.steps.map(step => ({
            originalStepId: step.id,
            mandatory: !!step.mandatory,
          }));
          setFieldValue('stepOverrides', initialOverrides);
        }
      },
    },
  );

  const steps = templateResponse?.data.steps || [];

  const handleMandatoryToggle = (stepId: number, currentMandatory: boolean) => {
    const newOverrides = values.stepOverrides.map(o => {
      if (o.originalStepId === stepId) {
        return { ...o, mandatory: !currentMandatory };
      }
      return o;
    });
    setFieldValue('stepOverrides', newOverrides);
  };

  const getIsMandatory = (stepId: number, originalMandatory?: boolean) => {
    const override = values.stepOverrides.find(o => o.originalStepId === stepId);
    return override ? override.mandatory : !!originalMandatory;
  };

  const getValidityPeriodText = (months: number | null | undefined) => {
    if (months === undefined || months === null) return t(action_library_messages.instance.form.validity_period_not_set);
    return t(action_library_messages.instance.form.validity_period_text, {
      months,
      unit: months === 1 ? t(action_library_messages.instance.form.month) : t(action_library_messages.instance.form.months),
    });
  };

  const renderMetadataTooltip = (template: ActionTemplate) => (
    <Box p={1}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.table.purpose)}:</strong> {template.purposeDisplay}
          </Typography>
        </Grid>
        {template.subAreaData && (
          <Grid item xs={6}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.table.subarea)}:</strong> {template.subAreaData.name}
            </Typography>
          </Grid>
        )}
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.instance.form.owner_type)}:</strong> {template.ownerTypeDisplay}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.table.criticality)}:</strong> {template.criticalityDisplay}
          </Typography>
        </Grid>
        {template.reference && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.detail.reference)}:</strong> {template.reference}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  if (isLoading) return <Loader />;

  return (
    <Box>
      <Box className={styles.section}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormTextInput
              formik={useFormikContext()}
              id='instanceName'
              label={t(action_library_messages.instance.form.instance_name)}
              required
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormDateInput
              dateTime={false}
              formik={useFormikContext()}
              fullWidth
              id='deadlineDate'
              label={t(action_library_messages.instance.form.deadline)}
              required
            />
            <Typography color='textSecondary' variant='body2'>
              {getValidityPeriodText(templateResponse?.data.validityPeriodMonths)}
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormDateInput
              dateTime={false}
              formik={useFormikContext()}
              fullWidth
              id='reminderDate'
              label={t(action_library_messages.instance.form.reminder)}
              required
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={styles.section}>
        <FormTextInput formik={useFormikContext()} id='reportedTo' label={t(action_library_messages.instance.form.reported_to)} />
      </Box>

      <Box className={styles.section}>
        <Typography style={{ marginBottom: '15px' }} variant='h6'>
          {t(action_library_messages.instance.form.special_instruction)}
        </Typography>

        <FormRichTextInput formik={useFormikContext() as any} id='specialInstruction' />
      </Box>
      <Box className={styles.section}>
        <Box className={styles.headerWithInfo}>
          <Typography gutterBottom variant='subtitle2'>
            {t(action_library_messages.instance.form.template_summary)}
          </Typography>
          {templateResponse?.data && (
            <HtmlTooltip title={renderMetadataTooltip(templateResponse.data)}>
              <HelpIcon className={styles.infoIcon} />
            </HtmlTooltip>
          )}
        </Box>
        <Paper className={styles.summaryPaper} variant='outlined'>
          <Typography
            color='textSecondary'
            dangerouslySetInnerHTML={{ __html: templateResponse?.data.shortDescription || '' }}
            variant='body2'
          />
        </Paper>
      </Box>

      <Box className={styles.section}>
        <Typography gutterBottom variant='h6'>
          {t(action_library_messages.instance.form.steps_section_title)}
        </Typography>
        <Typography color='textSecondary' paragraph variant='body2'>
          {t(action_library_messages.instance.form.steps_section_description)}
        </Typography>
        <Paper style={{ padding: 16 }} variant='outlined'>
          {steps.map((step: ActionTemplateStep) => {
            const isMandatory = getIsMandatory(step.id, step.mandatory);
            return (
              <Box key={step.id} className={styles.stepRow}>
                <Box className={styles.stepContent}>
                  <Typography variant='subtitle2'>{step.title}</Typography>
                  <Typography color='textSecondary' dangerouslySetInnerHTML={{ __html: step.shortDescription }} variant='body2' />
                </Box>
                <Box className={styles.stepCheckbox}>
                  <HtmlTooltip title={t(action_library_messages.instance.form.mandatory_tooltip)}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={isMandatory} color='primary' onChange={() => handleMandatoryToggle(step.id, isMandatory)} />
                      }
                      label={t(action_library_messages.instance.form.mandatory)}
                    />
                  </HtmlTooltip>
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Box>
    </Box>
  );
};

export default StepTimeParameters;
